import { APP_BASE_HREF } from '@angular/common'
import { Injectable, inject } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'

import { Store } from '@ngrx/store'

import { Observable } from 'rxjs'
import { map, tap } from 'rxjs/operators'

import {
  FlowMappings,
  ProcessDataModel,
  RuntimeComponentDefinition,
  TemplateConfig,
} from '@flowx/core-sdk'
import { DataMappingType } from '@flowx/designer/modules/core/dictionary/data-mapping.dictionary'
import { IDraggableComponent } from '@flowx/designer/modules/ui-designer/models/dndComponentsByCategory'
import { PaginationQueryParams } from '@flowx/designer/shared/api/api-http.service'
import {
  PaginatedEndpoint,
  PaginatedResponse,
  Sort,
} from '@flowx/designer/shared/services/paginated-datasource'
import { ComponentIdentifierEnum, TemplateConfigType } from '@flowx/shared/interfaces'

import { GridSearchService } from './grid-search.service'

import { ReusableTemplatesApiService } from '../api/reusable-templates.api'
import { ApplicationResource, withManifestAuditInfo } from '../entities/application.entity'
import { HostResourceType, ReusableTemplate } from '../entities/reusable-resources.entity'
import { selectWorkspaceId } from '../store/selectors/router.selectors'

const SORT_FIELD = 'modifiedDate'
const SORT_DIRECTION = 'desc'

@Injectable()
export class ReusableTemplatesService extends GridSearchService<ReusableTemplate> {
  #route = inject(ActivatedRoute)
  #router = inject(Router)
  #uiTemplatesApi = inject(ReusableTemplatesApiService)
  #baseHref = inject(APP_BASE_HREF)
  #store = inject(Store)

  getGridEndpointInfo(): {
    endpoint: PaginatedEndpoint<any, ReusableTemplate>
    sort: Sort
    query: Partial<any>
  } {
    return {
      endpoint: (request, query) =>
        this.getReusableTemplatesList({
          ...request,
          ...query,
          latest: true,
        }),
      sort: {
        property: SORT_FIELD,
        direction: SORT_DIRECTION,
      },
      query: { name: this.#route.snapshot.queryParams?.searchQuery ?? '' },
    }
  }

  getReusableTemplatesList(
    queryParams: PaginationQueryParams & { latest: boolean }
  ): Observable<PaginatedResponse<ReusableTemplate>> {
    return this.#uiTemplatesApi.getAllReusableTemplates(queryParams).pipe(
      map((response) => ({
        ...response.resourceContent,
        content: withManifestAuditInfo(response.resourceContent.content, response.manifestInfo),
      }))
    )
  }

  createReusableTemplate(uiTemplate: Partial<ReusableTemplate>): Observable<ReusableTemplate> {
    return this.#uiTemplatesApi
      .createReusableTemplate({
        name: uiTemplate.name,
        description: uiTemplate.description,
        appId: uiTemplate.appId,
        hostResourceType: uiTemplate.hostResourceType,
      })
      .pipe(
        map((response) => response.resourceContent),
        tap(() => this.refresh())
      )
  }

  updateReusableTemplate(uiTemplate: Partial<ReusableTemplate>): Observable<ReusableTemplate> {
    return this.#uiTemplatesApi
      .updateReusableTemplate(
        {
          name: uiTemplate.name,
          description: uiTemplate.description,
          appId: uiTemplate.appId,
        },
        uiTemplate.resourceId
      )
      .pipe(tap(() => this.refresh()))
  }

  deleteReusableTemplate(uiTemplate: ReusableTemplate): Observable<void> {
    return this.#uiTemplatesApi
      .deleteReusableTemplate(uiTemplate.resourceId)
      .pipe(tap(() => this.refresh()))
  }

  getReusableTemplateById(resourceId: string): Observable<ReusableTemplate> {
    return this.#uiTemplatesApi
      .getReusableTemplateById(resourceId)
      .pipe(map((response) => response.resourceContent))
  }

  getReusableTemplate(resourceId: string): Observable<ReusableTemplate> {
    return this.#uiTemplatesApi
      .getReusableTemplate(resourceId)
      .pipe(map((response) => response.resourceContent))
  }

  getReusableTemplatesListForConfig(
    hostResourceType: HostResourceType
  ): Observable<IDraggableComponent[]> {
    return this.#uiTemplatesApi.getReusableUiTemplates(hostResourceType).pipe(
      map(({ resourceContent: templates }) =>
        templates.map(
          (template) =>
            ({
              label: template.name,
              type: TemplateConfigType.FLOWX,
              resourceId: template.resourceId,
              reusableTemplateReference: {
                appId: template.appId,
                resourceDefinitionId: template.resourceDefinitionId,
                hostResourceType: template.hostResourceType,
              },
              description: template.description,
              componentIdentifier: ComponentIdentifierEnum.REUSABLE_TEMPLATE,
            }) satisfies IDraggableComponent
        )
      )
    )
  }

  goToDataModelParameters(
    reusableTemplateResource: ApplicationResource,
    type: DataMappingType
  ): void {
    const { appId, appVersionId, resourceId } = reusableTemplateResource
    const workspaceId = this.#store.selectSignal(selectWorkspaceId)()
    const link = this.#router.serializeUrl(
      this.#router.createUrlTree(
        [
          this.#baseHref,
          'guard',
          'workspace',
          workspaceId,
          'projects',
          appId,
          'config',
          appVersionId,
          'reusable-resources',
          'ui-templates',
          resourceId,
          'data-model',
        ],
        {
          queryParams: {
            tabIndex: type === DataMappingType.INPUT ? 1 : 2,
          },
        }
      )
    )
    window.open(link, '_blank')
  }

  goToApplicationReusableTemplate(
    reusableTemplateResource: Pick<ApplicationResource, 'appId' | 'appVersionId' | 'resourceId'>,
    selectedTemplateConfigId?: number
  ): void {
    const { appId, appVersionId, resourceId } = reusableTemplateResource
    const workspaceId = this.#store.selectSignal(selectWorkspaceId)()
    const link = this.#router.serializeUrl(
      this.#router.createUrlTree(
        [
          this.#baseHref,
          'guard',
          'workspace',
          workspaceId,
          'projects',
          appId,
          'config',
          appVersionId,
          'reusable-resources',
          'ui-templates',
          resourceId,
          'ui',
        ],
        {
          queryParams: {
            ...(selectedTemplateConfigId ? { templateIds: selectedTemplateConfigId } : {}),
          },
        }
      )
    )
    window.open(link, '_blank')
  }

  getReusableTemplateConfig(resourceId: string): Observable<{
    templateConfig: TemplateConfig
    customComponents: RuntimeComponentDefinition[]
    ccMappings: FlowMappings
  }> {
    return this.#uiTemplatesApi
      .testReusableTemplate(resourceId)
      .pipe(map((response) => response.resourceContent))
  }

  getReusableTemplateDataModel(resourceId: string): Observable<ProcessDataModel> {
    return this.#uiTemplatesApi
      .getReusableTemplateDataModel(resourceId)
      .pipe(map((response) => response.resourceContent))
  }
}
